/**
 * 支援機型 (/models, boards/Models.dc.html) — admin+. The L4 model-code list (lifted out of the
 * v1 Devices page). A machine NOT on the hardware-SN roster may redeem a software serial only if
 * its Win32_BaseBoard.Product is listed here. The list is small, so search / filter / CSV export
 * stay client-side; add shows the normalized match code live (same rule as the Worker).
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { CsvImport } from '@/components/CsvImport';
import { Modal } from '@/components/Modal';
import { TextInput } from '@/components/Field';
import { ToneTag } from '@/components/RosterBits';
import { TypeConfirmModal, errMsg } from '@/components/Dialogs';
import { usePoll } from '@/lib/usePoll';
import { api } from '@/lib/api';
import { buildCsv, downloadCsv } from '@/lib/csv';
import { fmtDay } from '@/lib/format';
import { normalizeModel } from '@/lib/models';
import type { ModelWhitelistEntry } from '@/types';

type Filter = 'all' | 'active' | 'revoked';

export function Models() {
  const { data, loading, error, refresh } = usePoll<ModelWhitelistEntry[]>(() => api.listModelWhitelist(), 30_000);
  const all = useMemo(() => [...(data ?? [])].sort((a, b) => a.display_model.localeCompare(b.display_model)), [data]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ModelWhitelistEntry | null>(null);
  const [rowErr, setRowErr] = useState<string | null>(null);

  const counts = { all: all.length, active: all.filter((m) => !m.revoked).length, revoked: all.filter((m) => m.revoked).length };
  const needle = q.trim().toLowerCase();
  const rows = all.filter((m) => {
    if (filter === 'active' && m.revoked) return false;
    if (filter === 'revoked' && !m.revoked) return false;
    if (!needle) return true;
    return [m.display_model, m.normalized_model, m.notes ?? ''].some((v) => v.toLowerCase().includes(needle));
  });

  const exportCsv = () => {
    const ts = new Date().toISOString().slice(0, 10);
    downloadCsv(
      `models-${ts}.csv`,
      buildCsv(
        ['機型碼', '比對用代碼', '狀態', '加入日期', '備註'],
        rows.map((m) => [m.display_model, m.normalized_model, m.revoked ? '已撤銷' : '啟用中', fmtDay(m.added_at), m.notes ?? '']),
      ),
    );
  };

  const restore = async (m: ModelWhitelistEntry) => {
    setRowErr(null);
    try { await api.restoreModelWhitelist(m.normalized_model); refresh(); }
    catch (e) { setRowErr(errMsg(e)); }
  };

  const pill = (id: Filter, label: string) => (
    <button type="button" className={'pill sm' + (filter === id ? ' on' : '')} onClick={() => setFilter(id)}>
      {label} <span className="n">{counts[id]}</span>
    </button>
  );

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <h1 className="v2-title">支援機型</h1>
          <p className="v2-sub">名單上沒有的機器,機型在這裡才可輸入軟體序號 · 比對的是主機板型號(Win32_BaseBoard.Product)</p>
        </div>
        <Button className="btn-outline" onClick={exportCsv} disabled={rows.length === 0}>匯出 CSV</Button>
        <Button className="btn-outline" onClick={() => setImportOpen(true)}>批次匯入 CSV</Button>
        <Button variant="primary" onClick={() => setAddOpen(true)}>新增機型</Button>
      </div>

      <section className="panel flush tpanel">
        <div className="tpanel-bar">
          <label className="searchbox" style={{ width: 280 }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜尋機型碼或名稱" aria-label="搜尋機型碼或名稱" />
          </label>
          {pill('all', '全部')}
          {pill('active', '啟用中')}
          {pill('revoked', '已撤銷')}
          <span className="grow" />
          {rowErr && <span className="error-line">{rowErr}</span>}
        </div>
        {Boolean(error) && !data && <div className="error-line" style={{ padding: 'var(--space-3) var(--space-4)' }}>載入失敗:{errMsg(error)}</div>}
        <div className="tscroll">
          <table className="v2-table">
            <thead>
              <tr><th>機型碼</th><th>比對用代碼(自動產生)</th><th>狀態</th><th>加入日期</th><th>備註</th><th /></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="empty-row"><td colSpan={6}>{loading && !data ? '載入中…' : all.length === 0 ? '尚無機型' : '沒有符合的機型'}</td></tr>
              ) : (
                rows.map((m) => (
                  <tr key={m.normalized_model}>
                    <td style={{ fontWeight: 'var(--weight-semibold)' }}>{m.display_model}</td>
                    <td className="mono muted">{m.normalized_model}</td>
                    <td><span className={m.revoked ? 'tone-muted' : 'tone-success'}>{m.revoked ? '已撤銷' : '啟用中'}</span></td>
                    <td className="muted">{fmtDay(m.added_at)}</td>
                    <td className="ellipsis muted">{m.notes || ''}</td>
                    <td style={{ textAlign: 'right' }}>
                      {m.revoked ? (
                        <button type="button" className="btn btn-outline" onClick={() => void restore(m)}>復原</button>
                      ) : (
                        <button type="button" className="btn btn-outline-danger" onClick={() => setRevokeTarget(m)}>撤銷…</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {addOpen && <AddModelModal onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); refresh(); }} />}

      <TypeConfirmModal
        key={revokeTarget?.normalized_model ?? 'none'}
        open={revokeTarget !== null}
        title="撤銷支援機型"
        body={<>撤銷後,不在硬體序號名單上的這個機型的機器將無法再輸入軟體序號。已在名單上的機器不受影響。請輸入比對用代碼確認。</>}
        expect={revokeTarget?.normalized_model ?? ''}
        confirmLabel="撤銷"
        onCancel={() => setRevokeTarget(null)}
        onConfirm={async () => {
          if (!revokeTarget) return;
          await api.revokeModelWhitelist(revokeTarget.normalized_model);
          setRevokeTarget(null);
          refresh();
        }}
      />

      <CsvImport
        open={importOpen}
        title="批次匯入機型"
        itemLabel="機型碼"
        hint="第一欄應為機型碼(Win32_BaseBoard.Product,例 GU605CX / B9400CBA);會自動轉成比對用代碼。"
        importOne={(m) => api.setModelWhitelist({ display_model: m })}
        onClose={() => setImportOpen(false)}
        onImported={refresh}
      />
    </div>
  );
}

function AddModelModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [model, setModel] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const norm = normalizeModel(model);
  const submit = async () => {
    if (!norm) { setErr('請輸入機型碼'); return; }
    setBusy(true); setErr(null);
    try {
      await api.setModelWhitelist({ display_model: model.trim(), notes: notes.trim() || undefined });
      onDone();
    } catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="新增機型"
      footer={<><Button onClick={onClose}>取消</Button><Button variant="primary" loading={busy} disabled={!norm} onClick={() => void submit()}>加入</Button></>}
    >
      <TextInput
        label="機型碼(主機板型號)"
        value={model}
        onChange={(e) => setModel(e.target.value)}
        placeholder="GU605CX"
        hint="Win32_BaseBoard.Product,例 GU605CX / FX507VU;不是銷售全名。桌機的主機板型號常和銷售型號不同,請以實機查到的值為準。"
        required
        autoComplete="off"
        spellCheck={false}
      />
      <p className="muted-note" style={{ marginTop: 0 }}>
        比對用代碼:<ToneTag tone="neutral"><span className="mono">{norm || '—'}</span></ToneTag>(轉大寫,空白與符號換成底線)
      </p>
      <TextInput label="備註(選填)" value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} placeholder="ROG Zephyrus G16" />
      {err && <div className="field-msg field-msg-error" role="alert">{err}</div>}
    </Modal>
  );
}
